import React from "react";
import { Card, Typography, Tag } from "antd";
import {
  BookOutlined,
  BulbOutlined,
  AimOutlined,
  ExperimentOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import "./CurriculumContent.css";

const { Title, Paragraph, Text } = Typography;

// Section headings that may appear in raw curriculum text
const SECTION_HEADER =
  /^\s*(LEARNING OBJECTIVES?|OBJECTIVES?|CONTENT|ACTIVITIES?|KEY LEARNING POINTS)\s*:?\s*$/i;

const cleanList = (list, minLength = 3) =>
  (Array.isArray(list) ? list : [])
    .filter((item) => typeof item === "string")
    .map((item) => item.trim())
    .filter((item) => item.length >= minLength && !SECTION_HEADER.test(item));

// Parse raw text such as "LEARNING OBJECTIVES:\n1. ...\n\nCONTENT:\n...\n\nACTIVITIES:\n..."
// Only sections that contain real text are returned.
const parseContent = (rawContent) => {
  const sections = {};
  if (typeof rawContent !== "string" || !rawContent.trim()) return sections;

  const grab = (header) => {
    const match = rawContent.match(
      new RegExp(
        `(?:^|\\n)\\s*${header}\\s*:?[ \\t]*\\n?([\\s\\S]*?)(?=\\n\\s*(?:LEARNING OBJECTIVES?|CONTENT|ACTIVITIES?)\\s*:|$)`,
        "i"
      )
    );
    return match ? match[1].trim() : "";
  };

  const objectivesText = grab("LEARNING OBJECTIVES?");
  if (objectivesText) {
    sections.objectives = cleanList(
      objectivesText
        .split(/[•\n]/)
        .map((obj) => obj.replace(/^\s*(?:\d+|[ivx]+|[a-z])[.)]\s*/i, "")),
      6
    );
  }

  const contentText = grab("CONTENT");
  if (contentText && !SECTION_HEADER.test(contentText)) {
    sections.content = contentText;
  }

  const activitiesText = grab("ACTIVITIES?");
  if (activitiesText) {
    sections.activities = cleanList(activitiesText.split(/[•\n]/), 11);
  }

  return sections;
};

const CurriculumContent = ({ content }) => {
  if (!content) return null;

  const isObject = typeof content === "object";
  const rawText = isObject
    ? typeof content.content === "string"
      ? content.content
      : typeof content.text === "string"
      ? content.text
      : ""
    : String(content);

  const parsed = parseContent(rawText);

  // Structured props (preferred) take precedence over parsed text
  const objectives = isObject && Array.isArray(content.objectives)
    ? cleanList(content.objectives, 6)
    : parsed.objectives || [];
  const activities = isObject && Array.isArray(content.activities)
    ? cleanList(content.activities, 11)
    : parsed.activities || [];
  const topics = isObject ? cleanList(content.topics, 2) : [];
  const bodyText = parsed.content || "";
  const hasStructuredSections = Boolean(
    parsed.objectives || parsed.content || parsed.activities
  );
  const unstructuredText =
    !hasStructuredSections && rawText.trim() ? rawText.trim() : "";

  const title = isObject ? content.topic || content.title : "";
  const subject = isObject ? content.subject : "";
  const grade = isObject ? content.grade || content.class : "";
  const hasAnything =
    objectives.length ||
    activities.length ||
    topics.length ||
    bodyText ||
    unstructuredText;

  return (
    <Card className="curriculum-card-simple">
      {/* Header with Topic and Basic Info */}
      <div className="curriculum-header-simple">
        <Title level={4} className="curriculum-title-simple">
          <BookOutlined className="title-icon" />
          {title || "Curriculum Content"}
        </Title>

        {/* Quick metadata tags (subject tag hidden when it just repeats the title) */}
        <div className="quick-metadata">
          {subject && subject !== title && (
            <Tag color="blue" className="subject-tag">
              {subject}
            </Tag>
          )}
          {grade && (
            <Tag color="green" className="grade-tag">
              {grade}
            </Tag>
          )}
        </div>
      </div>

      <div className="curriculum-body-simple">
        {/* Content Overview */}
        {bodyText && (
          <div className="content-section-simple">
            <div className="content-text">
              {bodyText.split("\n\n").map((paragraph, index) => (
                <Paragraph key={index} className="content-paragraph-simple">
                  {paragraph.trim()}
                </Paragraph>
              ))}
            </div>
          </div>
        )}

        {/* Topics covered */}
        {topics.length > 0 && (
          <div className="topics-simple">
            <Text strong className="topics-label">
              <UnorderedListOutlined /> Topics covered:
            </Text>
            <div className="objectives-tags">
              {topics.slice(0, 8).map((topic, index) => (
                <Tag key={index} className="topic-chip">
                  {topic}
                </Tag>
              ))}
              {topics.length > 8 && (
                <Tag className="more-tag">+{topics.length - 8} more</Tag>
              )}
            </div>
          </div>
        )}

        {/* Key learning points */}
        {objectives.length > 0 && (
          <div className="objectives-simple">
            <Text strong className="objectives-label">
              <AimOutlined /> Key Learning Points:
            </Text>
            <div className="objectives-tags">
              {objectives.slice(0, 3).map((objective, index) => (
                <Tag key={index} className="objective-tag" title={objective}>
                  {objective.length > 50
                    ? `${objective.substring(0, 50)}...`
                    : objective}
                </Tag>
              ))}
              {objectives.length > 3 && (
                <Tag className="more-tag">+{objectives.length - 3} more</Tag>
              )}
            </div>
          </div>
        )}

        {/* Activities, only when the curriculum actually lists some */}
        {activities.length > 0 && (
          <div className="activities-simple">
            <Text strong className="activities-label">
              <ExperimentOutlined /> Activities:
            </Text>
            <div className="activities-list-simple">
              {activities.slice(0, 2).map((activity, index) => (
                <div key={index} className="activity-item-simple">
                  <BulbOutlined className="activity-icon" />
                  <span>{activity}</span>
                </div>
              ))}
              {activities.length > 2 && (
                <Text className="more-activities">
                  +{activities.length - 2} more activities
                </Text>
              )}
            </div>
          </div>
        )}

        {/* Fallback for unstructured text content */}
        {unstructuredText && (
          <div className="fallback-content">
            <Paragraph className="fallback-text">{unstructuredText}</Paragraph>
          </div>
        )}

        {!hasAnything && (
          <Paragraph className="curriculum-empty-note">
            No detailed content is available for this item yet.
          </Paragraph>
        )}
      </div>
    </Card>
  );
};

export default CurriculumContent;
